import React, { ReactElement, MouseEvent, useState, useEffect } from "react";
import { ChecklistItem, ChecklistItemType, ChecklistItemUpdateType, UserRole } from "../../../common/types";
import { Panel, Header, CheckboxList } from "@glamcorner/components";
import "./refund-request-checklist-card.scss";
import { getChecklistTypeLabel, getUpdateTypeTitle, summarizeChecklist } from "../../utils";
import { UserNameWithDate } from "../UserNameWithDate";
import { allowedToModifyChecklistItem } from "../../../common/allowedToModifyChecklistItem";
import { stringToChecklistItemType } from "../../mappers";

interface RefundRequestChecklistCardProps {
  checklistItems: ChecklistItem[];
  itemOnClick: (type: ChecklistItemType) => void;
  isDisabled: boolean;
  userRoles: UserRole[];
}

export const RefundRequestChecklistCard: React.FC<RefundRequestChecklistCardProps> = ({
  checklistItems,
  itemOnClick,
  isDisabled,
  userRoles,
}) => {
  const [clickedWithShift, setClickedWithShift] = useState<boolean>(false);
  const [selectedType, setSelectedType] = useState<ChecklistItemType | undefined>(undefined);

  const hasItems = checklistItems.length > 0;

  const options = checklistItems.map(ci => {
    const label: { label: string; updateType: ChecklistItemUpdateType } = getChecklistTypeLabel(ci.type);
    const updateTypeTitle: string = getUpdateTypeTitle(label.updateType);
    const labelElement: ReactElement = (
      <div className="c-refund-request-checklist-card-label">
        {label.label}
        <div>
          {ci.user && ci.user.name && ci.checkedAt && <UserNameWithDate userName={ci.user.name} date={ci.checkedAt} />}
          <span className="c-refund-request-checklist-card-label__hint" title={updateTypeTitle}>
            {label.updateType}
          </span>
        </div>
      </div>
    );

    const isOptionDisabled = isDisabled || !allowedToModifyChecklistItem(ci.type, userRoles);

    return {
      value: ci.type,
      label: labelElement,
      isDisabled: isOptionDisabled,
    };
  });
  const selectedValues = checklistItems.filter(ci => ci.checkedAt).map(ci => ci.type);
  const checklistWrapperOnClickHandler = (event: MouseEvent) => {
    setClickedWithShift(event.shiftKey);
  };
  const checklistOnSelectHandler = (value: string) => {
    setSelectedType(stringToChecklistItemType(value));
  };

  useEffect(() => {
    if (clickedWithShift && selectedType) {
      itemOnClick(selectedType);

      setClickedWithShift(false);
      setSelectedType(undefined);
    }
  }, [clickedWithShift, selectedType]);

  return (
    <Panel className="c-refund-request-checklist-card" ready={true}>
      <Header onClick={() => {}} withCloseButton={false}>
        Checklist {hasItems && `(${summarizeChecklist(checklistItems)})`}
      </Header>
      <div className="c-refund-request-checklist-card__content">
        <div className="c-refund-request-screen__explain-text">
          {hasItems
            ? "Once all checklist items are completed, refund components will be scheduled for processing in the next batch"
            : "No checklist items need to be completed for this component. Refund will be scheduled for processing in the next batch"}
        </div>
        <div
          className="c-refund-reqeust-checklist-card__checkbox-list-wrapper"
          onClick={checklistWrapperOnClickHandler}
        >
          <CheckboxList options={options} selectedValues={selectedValues} onSelect={checklistOnSelectHandler} />
        </div>
      </div>
    </Panel>
  );
};
